// src/modals/_GuestFieldsForm.jsx — Phase 6.D PR #169a v1.4 (ADR-104)
//                                  + PR #169a-bis (ADR-105) edit unifié
//
// Sous-composant DRY pour formulaire 13 champs voyageur partagé entre :
//   - _PricingBlockCreateBody.jsx (création résa direct interne via blocage)
//   - ResaV2Modal.jsx > InfosTab (édition kind='pricing_block', ADR-105)
//
// PR #169a-bis : PricingBlockGuestEditModal.jsx supprimé, édition unifiée
// directement dans ResaV2Modal.
//
// Pattern controlled : state hissé au parent via `values` + `onChange(key, val)`.
// Permet : pré-fill (mode édition) + validation au submit côté parent.
//
// 14 champs (whitelist côté serveur : PRICING_BLOCK_GUEST_WHITELIST) :
//   - guest_first_name, guest_last_name        (identité)
//   - guest_email, guest_phone                 (contact)
//   - guest_address, guest_city, guest_postal_code, guest_country  (adresse)
//   - guest_language                           (fr/en/es)
//   - guest_count, children_count              (composition)
//   - notes                                    (libre, max 500)
//   - total_amount                             (NUMERIC 10,2)
//
// Préfixe `_` = convention internal-module (cohérent _PricingBlockCreateBody,
// _PricingManualEditBody, _block-helpers, _block-visual etc.).
//
// PR #169b (ADR-106) : bandeau warning "Comptabilisation en cours
// d'implémentation" Q-D13 retiré — agrégation effective via
// calcDirectInternePure + wrapper App.jsx `calc` enrichi.

import { Lightbulb } from "lucide-react";
import { DS } from "../lib/ui";

const LANGUAGES = [
  { value: "", label: "—" },
  { value: "fr", label: "Français" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
];

// Style générique input (cohérent avec _PricingBlockCreateBody).
const INPUT_STYLE = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: DS.radiusControl,
  border: `0.5px solid ${DS.borderHover}`,
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  background: DS.card,
};

const LABEL_STYLE = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: DS.marine,
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};

/**
 * Formulaire voyageur 13 champs (DRY pour Create + Edit).
 *
 * @param {object} props
 * @param {object} props.values — state des champs côté parent (clés = noms whitelist)
 * @param {function} props.onChange — handler(key, val) appelé sur chaque modif
 * @param {boolean} [props.disabled] — désactive tous les inputs (submit en cours)
 */
export default function GuestFieldsForm({ values = {}, onChange, disabled }) {
  const handle = (key) => (e) => {
    if (typeof onChange === "function") onChange(key, e.target.value);
  };
  const handleNumber = (key) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange && onChange(key, "");
      return;
    }
    const n = Number(raw);
    onChange && onChange(key, Number.isFinite(n) ? n : raw);
  };

  return (
    <div data-testid="guest-fields-form">
      <div
        style={{
          display: "flex",
          gap: 7,
          fontSize: 11,
          color: DS.marineDeep,
          marginBottom: 10,
          padding: 8,
          background: DS.cielSoft,
          borderRadius: 8,
        }}
      >
        <Lightbulb size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>Détails voyageur (optionnel). Remplir si résa direct interne FBZ.
        Aucune donnée n'est envoyée aux OTAs (Airbnb / Booking).</span>
      </div>

      {/* Identité — 2 colonnes */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Prénom</label>
          <input
            type="text"
            data-testid="guest-field-first-name"
            value={values.guest_first_name || ""}
            onChange={handle("guest_first_name")}
            disabled={disabled}
            maxLength={200}
            placeholder="ex: Marie"
            style={INPUT_STYLE}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Nom</label>
          <input
            type="text"
            data-testid="guest-field-last-name"
            value={values.guest_last_name || ""}
            onChange={handle("guest_last_name")}
            disabled={disabled}
            maxLength={200}
            placeholder="ex: Dupont"
            style={INPUT_STYLE}
          />
        </div>
      </div>

      {/* Contact — 2 colonnes */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Email</label>
          <input
            type="email"
            data-testid="guest-field-email"
            value={values.guest_email || ""}
            onChange={handle("guest_email")}
            disabled={disabled}
            maxLength={200}
            placeholder="ex: marie@dupont.fr"
            style={INPUT_STYLE}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Téléphone</label>
          <input
            type="tel"
            data-testid="guest-field-phone"
            value={values.guest_phone || ""}
            onChange={handle("guest_phone")}
            disabled={disabled}
            maxLength={200}
            placeholder="ex: +33 6 12 34 56 78"
            style={INPUT_STYLE}
          />
        </div>
      </div>

      {/* Adresse */}
      <div style={{ marginBottom: 10 }}>
        <label style={LABEL_STYLE}>Adresse</label>
        <input
          type="text"
          data-testid="guest-field-address"
          value={values.guest_address || ""}
          onChange={handle("guest_address")}
          disabled={disabled}
          maxLength={200}
          placeholder="ex: 12 rue de la Paix"
          style={INPUT_STYLE}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Code postal</label>
          <input
            type="text"
            data-testid="guest-field-postal-code"
            value={values.guest_postal_code || ""}
            onChange={handle("guest_postal_code")}
            disabled={disabled}
            maxLength={200}
            placeholder="31000"
            style={INPUT_STYLE}
          />
        </div>
        <div style={{ flex: 2 }}>
          <label style={LABEL_STYLE}>Ville</label>
          <input
            type="text"
            data-testid="guest-field-city"
            value={values.guest_city || ""}
            onChange={handle("guest_city")}
            disabled={disabled}
            maxLength={200}
            placeholder="Toulouse"
            style={INPUT_STYLE}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Pays (ISO)</label>
          <input
            type="text"
            data-testid="guest-field-country"
            value={values.guest_country || ""}
            onChange={handle("guest_country")}
            disabled={disabled}
            maxLength={2}
            placeholder="FR"
            style={INPUT_STYLE}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Langue</label>
          <select
            data-testid="guest-field-language"
            value={values.guest_language || ""}
            onChange={handle("guest_language")}
            disabled={disabled}
            style={INPUT_STYLE}
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Composition voyageurs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Adultes</label>
          <input
            type="number"
            data-testid="guest-field-count"
            value={values.guest_count ?? ""}
            onChange={handleNumber("guest_count")}
            disabled={disabled}
            min={0}
            max={50}
            placeholder="2"
            style={INPUT_STYLE}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={LABEL_STYLE}>Enfants</label>
          <input
            type="number"
            data-testid="guest-field-children-count"
            value={values.children_count ?? ""}
            onChange={handleNumber("children_count")}
            disabled={disabled}
            min={0}
            max={20}
            placeholder="0"
            style={INPUT_STYLE}
          />
        </div>
      </div>

      {/* Notes */}
      <div style={{ marginBottom: 10 }}>
        <label style={LABEL_STYLE}>Notes (max 500)</label>
        <textarea
          data-testid="guest-field-notes"
          value={values.notes || ""}
          onChange={handle("notes")}
          disabled={disabled}
          maxLength={500}
          rows={2}
          placeholder="ex: Arrivée tardive, demande lit bébé…"
          style={{ ...INPUT_STYLE, resize: "vertical" }}
        />
      </div>

      {/* Total — Q-D13 bandeau warning si > 0 */}
      <div style={{ marginBottom: 10 }}>
        <label style={LABEL_STYLE}>Total TTC (€)</label>
        <input
          type="number"
          data-testid="guest-field-total-amount"
          value={values.total_amount ?? ""}
          onChange={handleNumber("total_amount")}
          disabled={disabled}
          min={0}
          step="0.01"
          placeholder="0.00"
          style={INPUT_STYLE}
        />
      </div>
    </div>
  );
}
